; (function () {
  const PREVIEW_DELAY = 800

  const csrfToken = () => {
    const meta = document.querySelector('meta[name="csrf-token"]')

    return meta ? meta.content : ''
  }

  const ensureOk = response => {
    if (!response.ok) throw new Error('request failed')

    return response
  }

  const sendHtml = (url, method, html) => {
    return fetch(url, {
      method: method,
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'X-CSRF-Token': csrfToken(),
        Accept: 'text/vnd.turbo-stream.html, text/html'
      },
      body: new URLSearchParams({ html: html })
    })
  }

  const isTurboStream = response => {
    return (response.headers.get('Content-Type') || '').includes('turbo-stream')
  }

  const confirmAction = (title, confirmButtonText) => {
    return window.Swal.fire({
      title: title,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: confirmButtonText,
      cancelButtonText: 'Cancel'
    }).then(result => result.isConfirmed)
  }

  const documentSnapshot = editor => JSON.stringify(editor.getDocument())

  const hasText = editor => editor.getDocument().toString().trim().length > 0

  const trixEditor = () => ({
    ready: false,
    dirty: false,
    savedHtml: '',
    savedSnapshot: '',

    trix() {
      return this.$root.querySelector('trix-editor')
    },

    editor() {
      return this.trix().editor
    },

    setup() {
      const element = this.trix()

      if (this.ready || !element || !element.editor) return

      this.ready = true
      this.rememberSavedContent()
    },

    rememberSavedContent() {
      this.savedHtml = this.trix().value
      this.savedSnapshot = documentSnapshot(this.editor())
      this.dirty = false
    },

    changed() {
      this.setup()
      this.dirty = documentSnapshot(this.editor()) !== this.savedSnapshot
      this.schedulePreview()
    }
  })

  const modalWindow = () => ({
    inline: false,
    open: false,
    tab: 'edit',
    error: '',

    startInline() {
      this.open = true
      this.preview()
    },

    openModal() {
      this.setup()
      this.tab = 'edit'
      this.open = true
      this.preview()
    },

    finishEditing() {
      this.error = ''
      if (!this.inline) this.open = false
    },

    close() {
      if (!this.dirty) return this.finishEditing()

      confirmAction('Discard your unsaved changes?', 'Yes, discard').then(confirmed => {
        if (confirmed) this.discardChanges()
      })
    },

    discardChanges() {
      this.editor().loadHTML(this.savedHtml)
      this.dirty = false
      this.finishEditing()
    },

    failWith(message) {
      this.error = message
    }
  })

  const contentActions = () => ({
    insertVariable(name) {
      this.editor().insertString(`{${name}}`)
    },

    loadDefaultText() {
      this.editor().loadHTML(this.$root.dataset.defaultHtml)
    },

    startFromDefault() {
      if (!hasText(this.editor())) return this.loadDefaultText()

      confirmAction('Replace the current content with the default text?', 'Yes, replace').then(
        confirmed => {
          if (confirmed) this.loadDefaultText()
        }
      )
    },

    resetToDefault() {
      confirmAction('Remove the custom email and go back to the default one?', 'Yes, reset').then(
        confirmed => {
          if (confirmed) this.clearTemplate()
        }
      )
    },

    clearTemplate() {
      this.editor().loadHTML('')
      setTimeout(() => this.persist(''), 0)
    }
  })

  const livePreview = () => ({
    previewing: false,
    previewHtml: '',
    previewTimer: null,

    schedulePreview() {
      if (!this.open) return

      clearTimeout(this.previewTimer)
      this.previewTimer = setTimeout(() => this.preview(), PREVIEW_DELAY)
    },

    preview() {
      this.previewing = true

      sendHtml(this.$root.dataset.previewUrl, 'POST', this.trix().value)
        .then(ensureOk)
        .then(response => response.text())
        .then(html => this.showPreview(html))
        .catch(() => this.failWith('Could not load the preview.'))
        .finally(() => {
          this.previewing = false
        })
    },

    showPreview(html) {
      this.previewHtml = html
    }
  })

  const persistence = () => ({
    hasTemplate: false,
    saving: false,

    save() {
      this.persist(this.trix().value)
    },

    persist(html) {
      this.saving = true
      this.error = ''

      sendHtml(this.$root.dataset.saveUrl, 'PATCH', html)
        .then(response => this.showFlash(response))
        .then(response => this.finishSaving(response))
        .catch(() => this.failWith('Could not save. Please try again.'))
        .finally(() => {
          this.saving = false
        })
    },

    showFlash(response) {
      if (!isTurboStream(response)) return ensureOk(response)

      return response.text().then(stream => {
        Turbo.renderStreamMessage(stream)

        return response
      })
    },

    finishSaving(response) {
      ensureOk(response)

      this.hasTemplate = hasText(this.editor())
      this.rememberSavedContent()
      this.finishEditing()
    }
  })

  const scheduledEmailEditor = () => ({
    ...trixEditor(),
    ...modalWindow(),
    ...contentActions(),
    ...livePreview(),
    ...persistence(),

    init() {
      this.hasTemplate = this.$root.dataset.hasTemplate === 'true'
      this.inline = this.$root.dataset.inline === 'true'
      this.setup()

      if (this.inline) this.startInline()
    }
  })

  if (typeof window !== 'undefined') {
    window.scheduledEmailEditor = scheduledEmailEditor
  }
  return scheduledEmailEditor
})()
